import React, { Fragment, useState } from 'react';
import {
  createStyles,
  Divider,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { CustomDialog } from '../common/custom-dialog';
import colors from '../../../../themes/main-colors';
import CottageIcon from '../../../../static/outageIconsV2/cottage.svg';
import MyLocationIcon from '../../../../static/outageIconsV2/my_location.svg';
import MapLegendIcon from '../../../../static/outageIconsV2/map_legend.svg';
import HelpIcon from '../icons/HelpIcon';
import { CONSTANTS } from '../../constants';
import { ToggleSwitch } from '../common/toggle-switch';
import { useTranslation } from '../../../../hooks/useTranslation';
import { useIsMobile } from '../../../../util/style-utils';

const tips = [
  {
    icon: <MyLocationIcon width={24} height={24} />,
    title: 'CURRENT_LOCATION',
    desc: 'CURRENT_LOCATION_DESCRIPTION',
  },
  {
    icon: <CottageIcon width={24} height={24} />,
    title: 'ACCOUNT_LOCATION',
    desc: 'ACCOUNT_LOCATION_DESCRIPTION',
  },
  {
    icon: <MapLegendIcon width={24} height={24} />,
    title: 'MAP_AND_LEGEND_SETTINGS',
    desc: 'MAP_AND_LEGEND_SETTINGS_DESCRIPTION',
  },
  {
    icon: <HelpIcon width={24} height={24} />,
    title: 'EMERGENCY_ZONES',
    desc: 'EMERGENCY_ZONES_DESCRIPTION',
  },
];
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    section: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      backgroundColor: colors.white,
      padding: theme.spacing(2.5),
      '& h6': {
        fontWeight: 600,
        fontSize: '1.25rem',
      },
      '& p': {
        fontSize: '1rem',
      },
    },
    tipWrapper: {
      padding: theme.spacing(2.5),
      gap: '10px',
    },
    tipRow: {
      backgroundColor: colors.white,
      padding: theme.spacing(2.5),
      gap: '10px',
      '& h6': {
        fontWeight: 600,
        fontSize: '1.125rem',
      },
      '& p': {
        fontSize: '1rem',
      },
    },
    tipText: {
      marginLeft: theme.spacing(2),
    },
    restoration: {
      backgroundColor: colors.white,
      padding: theme.spacing(3),
      '& h6': {
        fontWeight: 600,
        fontSize: '1rem',
      },
    },
    toggleRow: {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: colors.white,
      padding: theme.spacing(2),
    },
    helpIcon: {
      fontSize: 30,
      color: colors.white,
    },
    legendIcon: {
      marginTop: 2,
    },
  }),
);

interface HelpTipsDialogProps {
  open: boolean;
  setOpen: (value: boolean) => void;
}

export const HelpTipsDialog = ({ open, setOpen }: HelpTipsDialogProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const isMobile = useIsMobile();

  const [toggleSwitchValue, setToggleSwitchValue] = useState(false);
  const dontShowRestorationTime =
    typeof window !== 'undefined' &&
    window.localStorage.getItem(CONSTANTS.DONT_SHOW_RESTORATION_TIME_MODAL);

  const handleClose = () => {
    setOpen(false);
    if (!dontShowRestorationTime || dontShowRestorationTime === '0') {
      typeof window !== 'undefined' &&
        window.localStorage.setItem(
          CONSTANTS.DONT_SHOW_RESTORATION_TIME_MODAL,
          toggleSwitchValue ? '1' : '0',
        );
    }
  };
  return (
    <Fragment>
      <CustomDialog
        open={open}
        onClose={handleClose}
        title={t('TIPS')}
        titleIcon={<HelpIcon className={classes.helpIcon} />}
      >
        <div className={classes.section}>
          <Typography variant="subtitle1">
            {t('WELCOME_TO_PGE_OUTAGE_MAP')}
          </Typography>
          <Typography variant="body1">{t('OUTAGE_MAP_INFORMATION')}</Typography>
        </div>

        {/* Tips */}
        <Grid container direction="column" className={classes.tipWrapper}>
          {tips.map((item, idx) => (
            <Grid container className={classes.tipRow} key={idx}>
              <Grid item className={classes.legendIcon}>
                {item.icon}
              </Grid>
              <Grid item xs>
                <Typography variant="subtitle1">{t(item.title)}</Typography>
                <Typography variant="body1">{t(item.desc)}</Typography>
              </Grid>
            </Grid>
          ))}
        </Grid>
        {(!dontShowRestorationTime || dontShowRestorationTime === '0') && (
          <>
            {!isMobile && (
              <>
                <div className={classes.restoration}>
                  <Typography variant="subtitle1" gutterBottom>
                    {t('RESTORATION_TIME_ARE_ESTIMATES')}
                  </Typography>
                  <Typography variant="body2">
                    {t('RESTORATION_TIME_INFO')}
                  </Typography>
                </div>
                <Divider />
              </>
            )}
            <div className={classes.toggleRow}>
              <ToggleSwitch
                checked={toggleSwitchValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setToggleSwitchValue(e.target.checked)
                }
              />
              <Typography variant="body1">
                {t('DONT_SHOW_THIS_AGAIN')}
              </Typography>
            </div>
          </>
        )}
      </CustomDialog>
    </Fragment>
  );
};
